import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Output,
    inject,
} from '@angular/core';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxFormModule } from 'devextreme-angular/ui/form';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    get as _get,
    isEmpty as _isEmpty,
    toString as _toString,
    trim as _trim,
} from 'lodash';
import {
    InvoiceRequestService,
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';
import moment from 'moment';
import {
    CreateInvoiceRequestBody,
    InvoiceDetails,
    InvoiceRequest,
    InvoiceRequestType,
    InvoiceType,
    ListInvoiceRequestsResponse,
    PatchInvoiceRequestBody,
} from 'src/app/shared/services/invoice-request/interfaces/api';
import { COLLECTOR_PLATFORMS } from 'src/app/shared/services/invoice-request/platform-map';
import {
    StoreConfig,
    StoreId,
    storeById,
    taxInvoiceEntityIdFor,
} from 'src/app/shared/config/store-config';
import { isVatRegisteredAt } from 'src/app/shared/config/entity-config';
import { UnifiedOrder } from '../combined-orders/unified-order';
import {
    AddressSegment,
    Counterparty,
    CounterpartyFinderComponent,
} from '../counterparty-finder/counterparty-finder.component';

// The editable shape behind the form — a flattened InvoiceDetails so DxForm can
// bind each field directly.
interface ReceiptInfoModel {
    // Which billing document the customer asked for. The collector defaults an
    // absent value to 'tax-invoice', which the grid then flags in red, so it is
    // always sent explicitly.
    requestType: InvoiceRequestType;
    type: InvoiceType | null;
    legalName: string;
    taxId: string;
    branchCode: string;
    branchName: string;
    line1: string;
    subDistrict: string;
    district: string;
    province: string;
    postalCode: string;
    email: string;
    phone: string;
}

// Captures the customer's billing details for one order and writes them to the
// billing-request collector, which the invoice print reads before falling back
// to the recognizer or the platform's own recipient data.
@Component({
    selector: 'app-receipt-info-popup',
    templateUrl: './receipt-info-popup.component.html',
    styleUrls: ['./receipt-info-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxPopupComponent,
        DxFormModule,
        DxButtonComponent,
        DxLoadPanelComponent,
        CounterpartyFinderComponent,
    ],
})
export class ReceiptInfoPopupComponent {
    private api = inject(InvoiceRequestService);
    private notification = inject(NotificationService);
    private util = inject(UtilService);
    private cdref = inject(ChangeDetectorRef);

    visible = false;

    // The persisted record travels with the order so the grid can update its
    // indicator without re-reading it.
    @Output() saved = new EventEmitter<{
        order: UnifiedOrder;
        request: InvoiceRequest;
    }>();

    order: UnifiedOrder | null = null;
    loading = false;
    saving = false;
    // The record being edited, when the order already has one.
    existing: InvoiceRequest | null = null;
    // Set when the existing record can no longer be changed (issued/erased).
    readOnlyReason = '';
    // Set when the collector holds a newer submission it did not apply, so the
    // details below are not the customer's latest.
    reviewNote = '';

    model: ReceiptInfoModel = ReceiptInfoPopupComponent.toModel(
        null,
        'receipt-only',
    );

    readonly invoiceTypes: InvoiceType[] = ['personal', 'company'];

    readonly requestTypes: { value: InvoiceRequestType; text: string }[] = [
        { value: 'receipt-only', text: 'ใบเสร็จรับเงิน · Receipt only' },
        { value: 'tax-invoice', text: 'ใบกำกับภาษี · Tax invoice' },
    ];

    // What the invoice print will actually issue for this order given the
    // document type selected — the same W14 rule the print uses
    // (taxInvoiceEntityIdFor), so the form cannot promise a document the
    // store's owner is not registered to issue.
    get requestTypeHint(): string {
        const order = this.order;
        const store = ReceiptInfoPopupComponent.storeFor(order);
        if (!order?.createDate || !store) {
            return '';
        }
        const dateIso = moment(order.createDate).format('YYYY-MM-DD');
        const wantsTaxInvoice = this.model.requestType === 'tax-invoice';
        const issuer = taxInvoiceEntityIdFor(store, dateIso, wantsTaxInvoice);
        if (issuer !== null) {
            return `Printing issues a full tax invoice (ใบกำกับภาษี) from ${issuer}.`;
        }
        const owner = store.entityId;
        return wantsTaxInvoice
            ? `${owner} is not VAT registered and no other entity issues for ` +
                  `${store.title}, so printing still issues a receipt ` +
                  `(ใบเสร็จรับเงิน) with these details. The request is only recorded.`
            : `Printing issues a receipt (ใบเสร็จรับเงิน) with these details ` +
                  `from ${owner}.`;
    }

    get title(): string {
        if (!this.order) {
            return 'Receipt info';
        }
        return `Receipt info — ${this.order.orderId} (${this.order.storeTitle})`;
    }

    get canSave(): boolean {
        return !this.saving && !this.loading && !this.readOnlyReason;
    }

    // Opens for an order so the form edits any record the order already has
    // rather than creating a duplicate — the collector rejects a second record
    // for the same (storeId, orderSn) anyway. `existing` is the record the
    // grid already resolved; only when it has none (the To Ship tab, where the
    // indicators never ran) does this ask the collector itself.
    open(order: UnifiedOrder, existing?: InvoiceRequest): void {
        this.order = order;
        this.existing = null;
        this.readOnlyReason = '';
        this.reviewNote = '';
        this.model = ReceiptInfoPopupComponent.toModel(
            null,
            ReceiptInfoPopupComponent.defaultRequestType(order),
        );
        this.visible = true;
        if (existing) {
            this.applyExisting(existing);
            return;
        }
        this.loading = true;
        this.api
            .list({ storeId: order.storeId, orderSn: order.orderId })
            .pipe(
                map((response: ListInvoiceRequestsResponse) =>
                    _get(response, 'items[0]', null),
                ),
                catchError((error) => {
                    this.toast(
                        `Could not load existing receipt info: ${this.util.errorToString(
                            error,
                        )}`,
                        MessageType.error,
                    );
                    return of(null);
                }),
            )
            .subscribe((found: InvoiceRequest | null) => {
                this.loading = false;
                if (found) {
                    this.applyExisting(found);
                }
                this.cdref.detectChanges();
            });
    }

    private applyExisting(existing: InvoiceRequest): void {
        this.existing = existing;
        this.model = ReceiptInfoPopupComponent.toModel(
            existing.invoice,
            existing.requestType,
        );
        this.reviewNote = existing.needsReview
            ? 'The customer sent newer details after this record was confirmed, ' +
              'and they were not applied. Review the submissions on the Invoice ' +
              'Request page before printing.'
            : '';
        if (existing.erased) {
            this.readOnlyReason =
                'This record was erased under PDPA and cannot be edited.';
        } else if (
            existing.status === 'issued' ||
            existing.status === 'cancelled'
        ) {
            this.readOnlyReason = `This record is ${existing.status} and can no longer be changed.`;
        }
        this.cdref.detectChanges();
    }

    close(): void {
        this.visible = false;
    }

    onVisibleChange(visible: boolean): void {
        this.visible = visible;
    }

    // The finder returns a company picked from DBD or the local counterparty
    // list; everything it fills stays editable.
    onCounterpartySelected(counterparty: Counterparty): void {
        const segment: Partial<AddressSegment> = counterparty.addrSegment || {};
        // The finder splits the street line from the house number; the
        // collector keeps both in line1. Fall back to the unsegmented address
        // when a source (e.g. the local counterparty list) didn't split it.
        const streetLine =
            _trim(
                `${_toString(segment.houseNo)} ${_toString(segment.addrLine)}`,
            ) || _toString(counterparty.address);
        this.model = {
            ...this.model,
            type: 'company',
            legalName: counterparty.companyName || this.model.legalName,
            taxId: counterparty.taxId || this.model.taxId,
            branchCode: ReceiptInfoPopupComponent.toBranchCode(
                counterparty.branchId,
                this.model.branchCode,
            ),
            branchName: counterparty.branchDisplayText || this.model.branchName,
            line1: streetLine || this.model.line1,
            subDistrict:
                _toString(segment.subDistrict) || this.model.subDistrict,
            district: _toString(segment.district) || this.model.district,
            province: _toString(segment.province) || this.model.province,
            postalCode: _toString(segment.postcode) || this.model.postalCode,
        };
        this.cdref.detectChanges();
    }

    save(): void {
        const order = this.order;
        if (!order || !this.canSave) {
            return;
        }
        this.saving = true;
        const request = this.existing
            ? this.patchExisting(this.existing)
            : this.createNew(order);
        request.subscribe(
            (saved: InvoiceRequest) => {
                this.saving = false;
                this.toast(
                    `Receipt info saved for ${order.orderId} (${saved.status})`,
                    MessageType.success,
                );
                this.close();
                this.saved.emit({ order, request: saved });
            },
            (error: unknown) => {
                this.saving = false;
                this.toast(this.util.errorToString(error), MessageType.error);
                this.cdref.detectChanges();
            },
        );
    }

    private createNew(order: UnifiedOrder): Observable<InvoiceRequest> {
        const body: CreateInvoiceRequestBody = {
            storeId: order.storeId,
            source: 'manual',
            platform: COLLECTOR_PLATFORMS[order.platform],
            orderSn: order.orderId,
            requestType: this.model.requestType,
            // Validate and land as `complete` — the print flow ignores
            // `collecting` drafts, so a draft here would change nothing.
            confirm: true,
            invoice: ReceiptInfoPopupComponent.toInvoiceDetails(this.model),
        };
        return this.api.create(body);
    }

    // A new record asks for the document the store's owner actually issues:
    // a tax invoice when the owner is VAT registered at the order date (the
    // print issues one regardless), else a receipt. Opting a non-registered
    // owner's order into a tax invoice — genlogic-co issuing for a requester
    // on shopee-genlogic — is the operator's explicit choice, never the
    // default, because it moves the document into another taxpayer's output
    // tax.
    private static defaultRequestType(order: UnifiedOrder): InvoiceRequestType {
        const store = ReceiptInfoPopupComponent.storeFor(order);
        if (!store || !order.createDate) {
            return 'receipt-only';
        }
        const dateIso = moment(order.createDate).format('YYYY-MM-DD');
        return isVatRegisteredAt(store.entityId, dateIso)
            ? 'tax-invoice'
            : 'receipt-only';
    }

    // null for a store outside the config — the caller degrades to the
    // receipt default rather than failing the form.
    private static storeFor(order: UnifiedOrder | null): StoreConfig | null {
        if (!order) {
            return null;
        }
        try {
            return storeById(order.storeId as StoreId);
        } catch {
            return null;
        }
    }

    // A `complete` record can only be patched by reopening it (which drops it
    // back to `collecting`), so it has to be confirmed again afterwards.
    private patchExisting(
        existing: InvoiceRequest,
    ): Observable<InvoiceRequest> {
        const patch: PatchInvoiceRequestBody = {
            requestType: this.model.requestType,
            invoice: ReceiptInfoPopupComponent.toInvoiceDetails(this.model),
        };
        if (existing.status === 'complete') {
            patch.reopen = true;
        }
        return this.api
            .update(existing.id, existing.version, patch)
            .pipe(
                switchMap((updated: InvoiceRequest) =>
                    updated.status === 'collecting'
                        ? this.api.confirm(updated.id, updated.version)
                        : of(updated),
                ),
            );
    }

    private toast(message: string, messageType: MessageType): void {
        this.notification.pushMessage({ message, messageType });
    }

    // Doubles as the empty model: every field falls back to '' or null.
    private static toModel(
        invoice: InvoiceDetails | null,
        requestType: InvoiceRequestType,
    ): ReceiptInfoModel {
        const source = invoice || {};
        const address = source.address || {};
        return {
            requestType,
            type: source.type ?? null,
            legalName: _toString(source.legalName),
            taxId: _toString(source.taxId),
            branchCode: _toString(source.branchCode),
            branchName: _toString(source.branchName),
            line1: _toString(address.line1),
            subDistrict: _toString(address.subDistrict),
            district: _toString(address.district),
            province: _toString(address.province),
            postalCode: _toString(address.postalCode),
            email: _toString(source.email),
            phone: _toString(source.phone),
        };
    }

    // Blank strings are sent as null so the API clears the field rather than
    // storing an empty value. branchCode must be absent for personal invoices.
    private static toInvoiceDetails(model: ReceiptInfoModel): InvoiceDetails {
        const isCompany = model.type === 'company';
        return {
            type: model.type,
            legalName: ReceiptInfoPopupComponent.orNull(model.legalName),
            taxId: ReceiptInfoPopupComponent.orNull(model.taxId),
            branchCode: isCompany
                ? ReceiptInfoPopupComponent.orNull(model.branchCode)
                : null,
            branchName: isCompany
                ? ReceiptInfoPopupComponent.orNull(model.branchName)
                : null,
            address: {
                line1: ReceiptInfoPopupComponent.orNull(model.line1),
                subDistrict: ReceiptInfoPopupComponent.orNull(
                    model.subDistrict,
                ),
                district: ReceiptInfoPopupComponent.orNull(model.district),
                province: ReceiptInfoPopupComponent.orNull(model.province),
                postalCode: ReceiptInfoPopupComponent.orNull(model.postalCode),
                country: 'TH',
            },
            email: ReceiptInfoPopupComponent.orNull(model.email),
            phone: ReceiptInfoPopupComponent.orNull(model.phone),
        };
    }

    private static orNull(value: string): string | null {
        const trimmed = _trim(_toString(value));
        return _isEmpty(trimmed) ? null : trimmed;
    }

    // Both finder paths already emit the 5-digit code the collector wants (or
    // '' when the source named no branch), so this only decides whether to
    // overwrite whatever is already in the form.
    private static toBranchCode(
        branchId: string | undefined,
        fallback: string,
    ): string {
        const code = _trim(_toString(branchId));
        return /^[0-9]{5}$/.test(code) ? code : fallback;
    }
}
